import React, { useEffect } from 'react'

import {
  Box,
  Button,
  Center,
  Flex,
  HStack,
  IconButton,
  Text,
  Tooltip,
} from '@chakra-ui/react'
import { MovieAreaExpand, MovieAreaShrink } from 'src/components/icon'
import { CameraButton, MicButton } from 'src/features/videoCall/components'
import { ScreenShareButton } from 'src/features/videoCall/components/ScreenShareButton/ScreenShareButton'
import { SharedScreenPlayer } from 'src/features/videoCall/components/SharedScreenPlayer/SharedScreenPlayer'
import { VideoCallMenu } from 'src/features/videoCall/components/VideoCallMenu/VideoCallMenu'
import { VideoCallRoomLocalPlayer } from 'src/features/videoCall/components/VideoCallRoomLocalPlayer/VideoCallRoomLocalPlayer'
import { VideoCallRoomRemotePlayer } from 'src/features/videoCall/components/VideoCallRoomRemotePlayer/VideoCallRoomRemotePlayer'
import { useLocalPlayer } from 'src/features/videoCall/context/localPlayerContext'
import { useVideoCallRoom } from 'src/features/videoCall/context/videoCallRoomContext'
import { useScreenSharePinning } from 'src/features/videoCall/hooks/useScreenSharePinning'
import { getButtonSize } from 'src/features/videoCall/utils/controlButtonUtil'
import { isScreenShareUserUid } from 'src/features/videoCall/utils/screenShare'

import { useVideoCallUsers } from '../../../videoCall/api/getVideoCallUsers'
import { useRoomMediaContext } from '../../context/MediaProvider'

type Props = {
  maxHeightPx: number
  maxWidthPx: number
  canShareScreen: boolean
  sendNotification: () => void
}

export const FloatingVideoPlayer: React.FC<Props> = ({
  maxHeightPx,
  maxWidthPx,
  canShareScreen = false,
  sendNotification,
}) => {
  const { remoteUsers, isScreenSharingActive, mainUser, setPinnedMainUserId } =
    useScreenSharePinning()

  const { data: videoCallUsers, mutate } = useVideoCallUsers({
    videoCallUserUids: remoteUsers.map(user => user.uid as number),
  })

  useEffect(() => {
    mutate()
  }, [remoteUsers, mutate])

  const {
    roomStatus,
    join,
    proxyStatus,
    agoraCloudProxyMode,
    videoCallUserUid,
  } = useVideoCallRoom()
  const { localScreenEnabled, stopLocalScreen } = useLocalPlayer()
  const { videoExpanded: floatingVideoExpanded } = useRoomMediaContext()

  const isJoinButtonDisabled =
    agoraCloudProxyMode !== 'Auto' && proxyStatus !== 'connected'

  return (
    <Flex justify="end">
      {/* 閉じている場合 */}
      <Box display={floatingVideoExpanded ? 'none' : 'block'} w="max-content">
        {!!mainUser && !floatingVideoExpanded && (
          <Box w="240px" h="135px">
            {isScreenShareUserUid(mainUser.uid) ? (
              <SharedScreenPlayer
                screenSharingRemoteUser={mainUser}
                colorScheme="green"
                isSmall
                currentUserUid={videoCallUserUid ?? undefined}
              />
            ) : (
              <VideoCallRoomRemotePlayer
                remoteUser={mainUser}
                isSmall
                userName={
                  videoCallUsers?.find(
                    user => user.videoCallUserUid === (mainUser.uid as number),
                  )?.name
                }
              />
            )}
          </Box>
        )}
        {localScreenEnabled && (
          <HStack
            w="full"
            as="button"
            onClick={() => {
              stopLocalScreen()
            }}
            bg="red.500"
            color="white"
            p="8px"
            align="center"
            justify="center"
          >
            <Text as="span">画面共有を停止</Text>
          </HStack>
        )}
        <HStack
          spacing="16px"
          borderRadius="sm"
          p="8px"
          bg="gray.800"
          justify="space-between"
        >
          <VideoCallMenu
            colorScheme="green"
            buttonType="floating-video-player"
          />

          <HStack spacing="16px">
            <MicButton type="floating-video-player" colorScheme="green" />

            {!!mainUser && (
              <CameraButton type="floating-video-player" colorScheme="green" />
            )}
          </HStack>

          {canShareScreen && roomStatus === 'joining' && (
            <ScreenShareButton
              type="floating-video-player"
              colorScheme="green"
            />
          )}
          <ToggleFloatingButton />
        </HStack>
      </Box>

      {/* 開いている場合 */}
      <Box
        bg="gray.800"
        color="white"
        borderRadius="sm"
        display={floatingVideoExpanded ? 'block' : 'none'}
        pos="relative"
      >
        <Flex align="end" borderBottom="1px" borderColor="gray.300">
          {!!mainUser && floatingVideoExpanded && roomStatus === 'joining' && (
            <Box w={`${maxWidthPx - 240}px`} h={`${maxHeightPx - 48}px`}>
              {isScreenShareUserUid(mainUser.uid) ? (
                <SharedScreenPlayer
                  screenSharingRemoteUser={mainUser}
                  colorScheme="green"
                  currentUserUid={videoCallUserUid ?? undefined}
                />
              ) : (
                <VideoCallRoomRemotePlayer
                  remoteUser={mainUser}
                  userName={
                    videoCallUsers?.find(
                      u => u.videoCallUserUid === (mainUser.uid as number),
                    )?.name
                  }
                />
              )}
            </Box>
          )}
          <Box w="240px">
            {roomStatus === 'joining' &&
              remoteUsers
                .filter(u => u.uid !== mainUser?.uid)
                .map(u => (
                  <Tooltip
                    key={u.uid}
                    label="メイン画面に固定"
                    placement="left"
                    hasArrow
                    isDisabled={!isScreenSharingActive}
                  >
                    <Box
                      as={isScreenSharingActive ? 'button' : undefined}
                      type={isScreenSharingActive ? 'button' : undefined}
                      w="full"
                      h="135px"
                      onClick={
                        isScreenSharingActive
                          ? () => setPinnedMainUserId(u.uid)
                          : undefined
                      }
                      cursor={isScreenSharingActive ? 'pointer' : undefined}
                      _hover={
                        isScreenSharingActive ? { opacity: 0.85 } : undefined
                      }
                      bg="transparent"
                      border="none"
                      p="0"
                    >
                      {isScreenShareUserUid(u.uid) ? (
                        <SharedScreenPlayer
                          screenSharingRemoteUser={u}
                          colorScheme="green"
                          isSmall
                          currentUserUid={videoCallUserUid ?? undefined}
                        />
                      ) : (
                        <VideoCallRoomRemotePlayer
                          remoteUser={u}
                          isSmall
                          userName={
                            videoCallUsers?.find(
                              user =>
                                user.videoCallUserUid === (u.uid as number),
                            )?.name
                          }
                        />
                      )}
                    </Box>
                  </Tooltip>
                ))}
            <Box w="full" h="135px">
              {roomStatus !== 'joining' ? (
                <Center w="full" h="full">
                  <Button
                    colorScheme="green"
                    isDisabled={isJoinButtonDisabled}
                    onClick={async () => {
                      if (remoteUsers.length == 0) sendNotification()
                      join()
                    }}
                  >
                    通話開始
                  </Button>
                </Center>
              ) : (
                <VideoCallRoomLocalPlayer isSmall />
              )}
            </Box>
          </Box>
        </Flex>

        <Flex p="8px" w="full" justify="space-between">
          <VideoCallMenu
            colorScheme="green"
            buttonType="floating-video-player"
          />

          <HStack spacing="16px">
            <MicButton type="floating-video-player" colorScheme="green" />
            <CameraButton type="floating-video-player" colorScheme="green" />
            {canShareScreen && roomStatus === 'joining' && (
              <Box>
                <ScreenShareButton
                  type="floating-video-player"
                  colorScheme="green"
                />
              </Box>
            )}
          </HStack>
          <ToggleFloatingButton />
        </Flex>
      </Box>
    </Flex>
  )
}

const ToggleFloatingButton: React.FC = () => {
  const buttonSize = getButtonSize('floating-video-player')
  const { videoExpanded, toggleVideoExpanded } = useRoomMediaContext()

  return (
    <Tooltip label={videoExpanded ? '縮小' : '拡大'}>
      <IconButton
        icon={videoExpanded ? <MovieAreaShrink /> : <MovieAreaExpand />}
        aria-label={videoExpanded ? '縮小する' : '拡大する'}
        h={buttonSize}
        w={buttonSize}
        colorScheme="green"
        variant="outline"
        minW={buttonSize}
        onClick={toggleVideoExpanded}
      />
    </Tooltip>
  )
}
